import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Download, Image as ImageIcon, Minimize2, Maximize2, Link, Unlink } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';

interface ResizePreset {
  name: string;
  width: number;
  height: number;
  description: string;
}

const resizePresets: ResizePreset[] = [
  { name: 'Instagram Post', width: 1080, height: 1080, description: 'Square format for Instagram posts' },
  { name: 'Instagram Story', width: 1080, height: 1920, description: 'Vertical format for Instagram stories' },
  { name: 'Facebook Cover', width: 1200, height: 630, description: 'Facebook page cover photo' },
  { name: 'Twitter Header', width: 1500, height: 500, description: 'Twitter profile header' },
  { name: 'YouTube Thumbnail', width: 1280, height: 720, description: 'YouTube video thumbnail' },
  { name: 'LinkedIn Banner', width: 1584, height: 396, description: 'LinkedIn profile banner' },
  { name: 'Website Header', width: 1920, height: 600, description: 'Website hero section' },
  { name: 'Blog Post', width: 1200, height: 800, description: 'Blog post featured image' },
  { name: 'Profile Picture', width: 400, height: 400, description: 'Social media profile picture' },
  { name: 'HD (720p)', width: 1280, height: 720, description: 'HD video resolution' },
  { name: 'Full HD (1080p)', width: 1920, height: 1080, description: 'Full HD video resolution' },
  { name: '4K (2160p)', width: 3840, height: 2160, description: '4K ultra HD resolution' },
];

export const ImageResizeTool: React.FC = () => {
  const { t } = useTranslation();
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [resizedImage, setResizedImage] = useState<string | null>(null);
  const [originalSize, setOriginalSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [targetWidth, setTargetWidth] = useState<number>(800);
  const [targetHeight, setTargetHeight] = useState<number>(600);
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [resizeMethod, setResizeMethod] = useState<'percentage' | 'pixels'>('pixels');
  const [percentage, setPercentage] = useState(100);
  const [quality, setQuality] = useState(0.9);
  const [isProcessing, setIsProcessing] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error(t('tools.imageResize.invalidImageError'));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          setOriginalSize({ width: img.width, height: img.height });
          setTargetWidth(img.width);
          setTargetHeight(img.height);
        };
        img.src = e.target?.result as string;
        setOriginalImage(e.target?.result as string);
        setResizedImage(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleWidthChange = (newWidth: number) => {
    setTargetWidth(newWidth);
    if (maintainAspectRatio && originalSize.width > 0) {
      const aspectRatio = originalSize.height / originalSize.width;
      setTargetHeight(Math.round(newWidth * aspectRatio));
    }
  };

  const handleHeightChange = (newHeight: number) => {
    setTargetHeight(newHeight);
    if (maintainAspectRatio && originalSize.height > 0) {
      const aspectRatio = originalSize.width / originalSize.height;
      setTargetWidth(Math.round(newHeight * aspectRatio));
    }
  };

  const handlePercentageChange = (newPercentage: number) => {
    setPercentage(newPercentage);
    if (originalSize.width > 0 && originalSize.height > 0) {
      setTargetWidth(Math.round(originalSize.width * (newPercentage / 100)));
      setTargetHeight(Math.round(originalSize.height * (newPercentage / 100)));
    }
  };

  const applyPreset = (preset: ResizePreset) => {
    setTargetWidth(preset.width);
    setTargetHeight(preset.height);
    setResizeMethod('pixels');
  };

  const resizeImage = async () => {
    if (!originalImage || !canvasRef.current) return;

    setIsProcessing(true);

    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const img = new Image();
      img.onload = () => {
        let finalWidth = targetWidth;
        let finalHeight = targetHeight;

        if (resizeMethod === 'percentage') {
          finalWidth = Math.round(originalSize.width * (percentage / 100));
          finalHeight = Math.round(originalSize.height * (percentage / 100));
        }

        canvas.width = finalWidth;
        canvas.height = finalHeight;

        // Use high-quality image scaling
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(img, 0, 0, finalWidth, finalHeight);

        const resizedDataUrl = canvas.toDataURL('image/jpeg', quality);
        setResizedImage(resizedDataUrl);
        setIsProcessing(false);
      };

      img.src = originalImage;
    } catch (error) {
      console.error('Resize error:', error);
      toast.error(t('tools.imageResize.resizeError'));
      setIsProcessing(false);
    }
  };

  const downloadResized = () => {
    if (!resizedImage) return;
    
    const link = document.createElement('a');
    link.download = `resized-image-${targetWidth}x${targetHeight}.jpg`;
    link.href = resizedImage;
    link.click();
  };

  const getFileSizeEstimate = (width: number, height: number): string => {
    // Rough estimate based on JPEG compression
    const pixels = width * height;
    const bytesPerPixel = quality * 3; // RGB channels with compression
    const estimatedBytes = pixels * bytesPerPixel;
    
    if (estimatedBytes < 1024) return `${Math.round(estimatedBytes)} B`;
    if (estimatedBytes < 1024 * 1024) return `${Math.round(estimatedBytes / 1024)} KB`;
    return `${(estimatedBytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Minimize2 className="w-5 h-5" />
            <span>Image Resize Tool</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <ImageIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('common.dragDropImage')}
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
              >
                <Upload className="w-4 h-4 mr-2" />
                {t('common.selectImage')}
              </Button>
            </div>

            {originalImage && (
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Original size: <span className="font-medium">{originalSize.width} × {originalSize.height}px</span>
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Resize Settings */}
      {originalImage && (
        <Card>
          <CardHeader>
            <CardTitle>Resize Settings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Resize Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Resize Method
                </label>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setResizeMethod('pixels')}
                    variant={resizeMethod === 'pixels' ? 'primary' : 'outline'}
                    size="sm"
                  >
                    Pixels
                  </Button>
                  <Button
                    onClick={() => setResizeMethod('percentage')}
                    variant={resizeMethod === 'percentage' ? 'primary' : 'outline'}
                    size="sm"
                  >
                    Percentage
                  </Button>
                </div>
              </div>

              {/* Resize Controls */}
              {resizeMethod === 'pixels' ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Width (px)
                    </label>
                    <Input
                      type="number"
                      value={targetWidth}
                      onChange={(e) => handleWidthChange(parseInt(e.target.value) || 0)}
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Height (px)
                    </label>
                    <Input
                      type="number"
                      value={targetHeight}
                      onChange={(e) => handleHeightChange(parseInt(e.target.value) || 0)}
                      min="1"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={() => setMaintainAspectRatio(!maintainAspectRatio)}
                      variant={maintainAspectRatio ? 'primary' : 'outline'}
                      className="w-full"
                    >
                      {maintainAspectRatio ? <Link className="w-4 h-4 mr-2" /> : <Unlink className="w-4 h-4 mr-2" />}
                      {maintainAspectRatio ? 'Locked' : 'Unlocked'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Scale: {percentage}%
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="500"
                    step="10"
                    value={percentage}
                    onChange={(e) => handlePercentageChange(parseInt(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>10%</span>
                    <span>500%</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    New size: {Math.round(originalSize.width * (percentage / 100))} × {Math.round(originalSize.height * (percentage / 100))}px
                  </p>
                </div>
              )}

              {/* Quality Setting */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Quality: {Math.round(quality * 100)}%
                </label>
                <input
                  type="range"
                  min="0.1"
                  max="1"
                  step="0.1"
                  value={quality}
                  onChange={(e) => setQuality(parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>

              {/* Resize Button */}
              <Button
                onClick={resizeImage}
                disabled={isProcessing}
                variant="primary"
                className="w-full"
              >
                {isProcessing ? 'Resizing...' : 'Resize Image'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Presets */}
      {originalImage && (
        <Card>
          <CardHeader>
            <CardTitle>Quick Presets</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {resizePresets.map((preset) => (
                <Button
                  key={preset.name}
                  onClick={() => applyPreset(preset)}
                  variant="outline"
                  className="h-auto p-3 text-left"
                >
                  <div>
                    <div className="font-medium">{preset.name}</div>
                    <div className="text-xs text-gray-500">
                      {preset.width} × {preset.height}px
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {preset.description}
                    </div>
                  </div>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview */}
      {originalImage && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Original Image</CardTitle>
            </CardHeader>
            <CardContent>
              <img
                src={originalImage}
                alt="Original"
                className="w-full h-auto max-h-64 object-contain rounded border"
              />
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                Size: {originalSize.width} × {originalSize.height}px
              </p>
            </CardContent>
          </Card>

          {resizedImage && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Resized Image</span>
                  <Button
                    onClick={downloadResized}
                    variant="outline"
                    size="sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <img
                  src={resizedImage}
                  alt="Resized"
                  className="w-full h-auto max-h-64 object-contain rounded border"
                />
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Size: {targetWidth} × {targetHeight}px<br />
                  Estimated file size: {getFileSizeEstimate(targetWidth, targetHeight)}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};
